import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { decryptPayload } from '../../payment-gateways/core/utils/crypto';
import {
  PaymentError,
  PaymentErrorCode,
} from '../../payment-gateways/core/utils/errors';
import { PaymentErrorScreen } from '../../payment-gateways/core/components/PaymentErrorScreen';
import { GatewayFactory } from '../../payment-gateways/core/components/GatewayFactory';
import { GatewaySelector } from '../../payment-gateways/core/components/GatewaySelector';
import { getGatewaysForCountry } from '../../payment-gateways/core/registry';
import type {
  GatewayManifestEntry,
  GatewayReturnPayload,
  OrderContext,
} from '../../payment-gateways/core/types';
import {
  ENTRY_PARAMS,
  isSupportedPayloadVersion,
  type EntryPayload,
} from '../../contracts/payment-contract';
import { normalizeCountry, resolveCountry } from '../../country/countryConfig';
import { savePendingOrder } from '../../payment-gateways/core/utils/pendingOrderStore';

type Resolution = { order: OrderContext } | { error: PaymentError };

/**
 * Entry point reached from the booking app's redirect. Replaces
 * Payment.jsx + IndiaPayment.js + ThailandPayment.js: decrypts the entry
 * payload once (one path, not two — see the plan's §1/§2), resolves the
 * country and gateway list from the registry instead of a hardcoded map,
 * and — critically — always resolves to either an order or an explicit
 * PaymentError. There is no code path here that falls through to a blank
 * page.
 */
export default function PaymentPage() {
  const navigate = useNavigate();
  const resolution = useMemo<Resolution>(resolveOrder, []);
  const [selected, setSelected] = useState<GatewayManifestEntry | null>(null);
  const [runtimeError, setRuntimeError] = useState<PaymentError | null>(null);

  const error =
    runtimeError ?? ('error' in resolution ? resolution.error : null);

  return (
    <div className="bg-app-gradient relative min-h-screen w-full">
      {error ? (
        <PaymentErrorScreen error={error} onBack={() => navigate(-1)} />
      ) : (
        <Body
          order={(resolution as { order: OrderContext }).order}
          selected={selected}
          onSelect={setSelected}
          onSettled={(entry, payload) => {
            // Razorpay settles in-page (no real navigation happened yet) —
            // this is the one hop from here into /payment/callback, and
            // it's same-origin/same-router, so it's a client-side
            // navigate(), not a full window.location redirect. Contrast
            // with GatewayFactory's hosted-checkout redirect and
            // bookingStatusRedirect.ts, both of which leave this app
            // entirely and have to stay window.location.href.
            navigate(
              buildCallbackPath(
                entry,
                (resolution as { order: OrderContext }).order,
                payload
              )
            );
          }}
          onError={setRuntimeError}
        />
      )}
    </div>
  );
}

function Body({
  order,
  selected,
  onSelect,
  onSettled,
  onError,
}: {
  order: OrderContext;
  selected: GatewayManifestEntry | null;
  onSelect: (entry: GatewayManifestEntry) => void;
  onSettled: (
    entry: GatewayManifestEntry,
    payload: GatewayReturnPayload
  ) => void;
  onError: (error: PaymentError) => void;
}) {
  const gateways = useMemo(
    () => getGatewaysForCountry(order.country),
    [order.country]
  );

  if (gateways.length === 0) {
    return (
      <PaymentErrorScreen
        error={
          new PaymentError(
            PaymentErrorCode.CONFIG_ERROR,
            'No payment method is configured for this region.',
            {
              country: order.country,
            }
          )
        }
      />
    );
  }

  const active = selected ?? (gateways.length === 1 ? gateways[0] : null);

  if (!active) {
    return <GatewaySelector entries={gateways} onSelect={onSelect} />;
  }

  return (
    <GatewayFactory
      entry={active}
      order={order}
      onSettled={(payload) => onSettled(active, payload)}
      onError={onError}
    />
  );
}

function resolveOrder(): Resolution {
  const search = new URLSearchParams(window.location.search);
  const orderId = search.get(ENTRY_PARAMS.orderId);
  const encrypted = search.get(ENTRY_PARAMS.encryptedPayload);

  if (!orderId || !encrypted) {
    return {
      error: new PaymentError(
        PaymentErrorCode.INVALID_ORDER,
        'This payment link is missing required details.'
      ),
    };
  }

  const payload = decryptPayload<EntryPayload>(encrypted);

  if (!payload || !isSupportedPayloadVersion(payload)) {
    return {
      error: new PaymentError(
        PaymentErrorCode.CONFIG_ERROR,
        'We could not verify this payment link.',
        { orderId }
      ),
    };
  }

  // Hostname is the source of truth (each country is its own deployment);
  // the payload's country is only a fallback for the rare case hostname
  // resolution itself can't determine one (e.g. a preview/staging host).
  const country =
    resolveCountry(window.location.hostname, search) ??
    normalizeCountry(payload.country);

  if (!country) {
    return {
      error: new PaymentError(
        PaymentErrorCode.CONFIG_ERROR,
        'We could not determine which region this payment belongs to.',
        {
          orderId,
        }
      ),
    };
  }

  const order: OrderContext = {
    orderId,
    authToken: payload.authToken,
    dbPtr: payload.db_ptr,
    amount: payload.amount,
    country,
  };

  savePendingOrder(order);

  return { order };
}

function buildCallbackPath(
  entry: GatewayManifestEntry,
  order: OrderContext,
  payload: GatewayReturnPayload
): string {
  // entry.load() already resolved once inside GatewayFactory; re-deriving
  // the URL here only needs the pure buildReturnUrl/buildCancelUrl string
  // builders, which are cheap and synchronous on every adapter — but those
  // live behind the async `load()`. Both adapters' URL shape is identical
  // by construction (`/payment/callback?gateway=<id>&order_id=<id>`), so it's
  // built directly here rather than re-awaiting the dynamic import.
  //
  // Relative, not absolute — this only ever feeds react-router's
  // navigate() (a same-origin, in-app hop), unlike the adapters' own
  // buildReturnUrl/buildCancelUrl, which have to be absolute because
  // they're handed to an external gateway.
  const params = new URLSearchParams({
    gateway: entry.id,
    order_id: order.orderId,
    status: payload.claimedStatus,
  });
  if (payload.gatewayPaymentId)
    params.set('payment_id', payload.gatewayPaymentId);

  return `/payment/callback?${params.toString()}`;
}
